/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import isEmpty from "lodash/fp/isEmpty";
import union from "lodash/fp/union";

import { DayTime, Interval } from "~/constants";
import DashboardApi from "~/features/dashboard/DashboardApi";
import transformToGeoLocation from "~/features/dashboard/transformers/transformToGeoLocation";
import transformToPlatformStats from "~/features/dashboard/transformers/transformToPlatformStats";
import transformToStats from "~/features/dashboard/transformers/transformToStats";
import calculatePeriodStats from "~/features/dashboard/utils/calculatePeriodStats";
import { ReportTypeEnum } from "~/features/reportTracker/constants";
import ReportApi from "~/features/reportTracker/ReportApi";
import { ReportRequest } from "~/features/reportTracker/types";
import { Geo, PlatformStats, PostDashboardReportResponseData, TotalStats } from "~/features/statistics/types";
import getDateInterval from "~/utils/getDateInterval";

export const loadQuickStatsAsync = createAsyncThunk("dashboard/loadQuickStats", async () => {
  const lastThirtyDays = getDateInterval(Interval.LAST_30_DAYS);
  const previousPeriod = getDateInterval(Interval.PREV_PERIOD);
  const currentPeriod = getDateInterval(Interval.CURRENT_PERIOD);
  const yesterday = getDateInterval(Interval.YESTERDAY);
  const today = getDateInterval(Interval.TODAY);

  const quickStatsPayload: ReportRequest = {
    excludedSites: [],
    from: `${lastThirtyDays.from} ${DayTime.START}`,
    to: `${today.to} ${DayTime.END}`,
    groupBy: "tm",
    skipCaching: true,
    limit: 9999,
    page: 0,
    reportType: ReportTypeEnum.totalStats,
  };

  const previousPeriodPayload: ReportRequest = {
    excludedSites: [],
    from: `${previousPeriod.from} ${DayTime.START}`,
    to: `${previousPeriod.to} ${DayTime.END}`,
    groupBy: "tm",
    skipCaching: true,
    limit: 9999,
    page: 0,
    reportType: ReportTypeEnum.totalStats,
  };

  const currentPeriodPayload: ReportRequest = {
    excludedSites: [],
    from: `${currentPeriod.from} ${DayTime.START}`,
    to: `${currentPeriod.to} ${DayTime.END}`,
    groupBy: "tm",
    skipCaching: true,
    limit: 9999,
    page: 0,
    reportType: ReportTypeEnum.totalStats,
  };

  const quickStatsPromise = ReportApi.postGetAutomaticReport(quickStatsPayload);
  const previousPeriodPromise = ReportApi.postGetAutomaticReport(previousPeriodPayload);
  const currentPeriodPromise = ReportApi.postGetAutomaticReport(currentPeriodPayload);

  const [quickStatsRes, prevPeriodRes, currPeriodRes] = await Promise.all([
    quickStatsPromise,
    previousPeriodPromise,
    currentPeriodPromise,
  ]);

  const data = quickStatsRes.data.result.data as TotalStats["data"];

  if (!data) {
    return {
      lastThirtyDaysData: {
        netRevenue: 0,
        grossRevenue: 0,
        premiumJoins: 0,
      },
      previousPeriodPayout: 0,
      currentPeriodPayout: 0,
      yesterdayData: {
        netRevenue: 0,
        grossRevenue: 0,
        premiumJoins: 0,
      },
      todayData: {
        netRevenue: 0,
        grossRevenue: 0,
        premiumJoins: 0,
      },
      payoutProgram: false,
      hasSites: false,
    };
  }

  const lastThirtyDaysData = calculatePeriodStats(data.items, lastThirtyDays.from, lastThirtyDays.to);
  const yesterdayData = calculatePeriodStats(data.items, yesterday.from, yesterday.to);
  const todayData = calculatePeriodStats(data.items, today.from, today.to);

  const previousPeriodPayout = (prevPeriodRes.data.result.data as TotalStats["data"]).summary.netPayout;
  const currentPeriodPayout = (currPeriodRes.data.result.data as TotalStats["data"]).summary.netPayout;

  //if one of the sites have payout program, display number in "at a galance" sections, else display N/A (Estimated Payout)
  const payoutProgram = data.items.some((site) => site.payoutProgramType !== null && site.payoutProgramType !== "");
  const hasSites = data.items.length > 0;

  return {
    lastThirtyDaysData,
    previousPeriodPayout,
    currentPeriodPayout,
    yesterdayData,
    todayData,
    payoutProgram,
    hasSites,
  };
});

export const loadSitesAsync = createAsyncThunk("dashboard/loadReferrerSites", async () => {
  const responses = await Promise.all([DashboardApi.getReferrerSites(), DashboardApi.getPromotionSites()]);

  const referrerSites = Object.keys(responses[0].data.sites).map(
    (key) => responses[0].data.sites[Number(key)] as string,
  );
  const promotionSites = responses[1].data.promotions.map((promo) => promo.domain);

  const sites = union(referrerSites, promotionSites);

  return {
    sites: sites,
  };
});

export const loadAutomaticDashboardReportAsync = createAsyncThunk(
  "report/postLoadAutomaticDashboardReport",
  async (payload: ReportRequest) => {
    try {
      const res = await ReportApi.postGetAutomaticReport(payload);
      const { result } = res.data;

      const reports = result.data as PostDashboardReportResponseData | null;
      return {
        status: result.status,
        cacheKey: result.cacheKey,
        totalStats: processTotalStatsData(reports?.totalStatsData),
        platformStats: processPlatformStatsData(reports?.platformStatsData),
        geoReport: processGeoReportsData(reports?.geoReportData),
      };
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
      throw new Error((error as any).response.status);
    }
  },
);

export const loadDashboardReportAsync = createAsyncThunk(
  "report/postLoadDashboardReport",
  async (payload: ReportRequest) => {
    try {
      const res = await ReportApi.postGetReport(payload);
      const { result } = res.data;

      const reports = result.data as PostDashboardReportResponseData | null;

      return {
        status: result.status,
        cacheKey: result.cacheKey,
        totalStats: processTotalStatsData(reports?.totalStatsData),
        platformStats: processPlatformStatsData(reports?.platformStatsData),
        geoReport: processGeoReportsData(reports?.geoReportData),
      };
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
      throw new Error((error as any).response.status);
    }
  },
);

const processTotalStatsData = (stats?: TotalStats["data"]) => {
  if (isEmpty(stats) || isEmpty(stats.items)) {
    return {
      byDate: {},
      sortedDates: [],
      sites: [],
    };
  }

  const sites = stats.items.map((site) => site.siteDomain).sort((a, b) => a.localeCompare(b));
  const byDate = transformToStats(stats);
  const sortedDates = Object.keys(byDate).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  return {
    byDate,
    sortedDates,
    sites,
  };
};

const processPlatformStatsData = (platformStats?: PlatformStats["data"]) => {
  if (isEmpty(platformStats) || isEmpty(platformStats.items)) {
    return {
      devices: {
        byDeviceType: {},
        sortedDeviceTypes: [],
      },
    };
  }

  const byDeviceType = transformToPlatformStats(platformStats);
  const sortedDeviceTypes = Object.keys(byDeviceType).sort(
    (a, b) => byDeviceType[b].netRevenue - byDeviceType[a].netRevenue,
  );

  return {
    devices: {
      byDeviceType,
      sortedDeviceTypes,
    },
  };
};

const processGeoReportsData = (countriesData?: Geo["data"]) => {
  if (isEmpty(countriesData) || isEmpty(countriesData.items)) {
    return {
      countries: {
        byCountryCode: {},
        sortedCountryCodes: [],
      },
    };
  }

  const byCountryCode = transformToGeoLocation(countriesData);
  const sortedCountryCodes = Object.keys(byCountryCode).sort(
    (a, b) => byCountryCode[b].netRevenue - byCountryCode[a].netRevenue,
  );

  return {
    countries: {
      byCountryCode,
      sortedCountryCodes,
    },
  };
};
